import { createSelector } from 'reselect';
import { getSuggestedItemsOptions } from 'store/suggested-items/selectors';
import { getDeletedItemIds } from 'store/items/selectors';
import { TYPE, FILTER_KEYS } from './fixtures';

export const getBrowseItemsInitial = ({ browseItems }) => browseItems.data;
export const getBrowseItemsLoadingState = ({ browseItems }) => browseItems.loading;
export const getBrowseItemsFilters = ({ browseItems }) => browseItems.filters;
export const getSelectedBrowseItems = ({ browseItemsActions }) => browseItemsActions.selectedIds;
export const getOpenedBrowseItem = ({ browseItemsActions }) => browseItemsActions.openedItemId;

export const getImages = (images = []) => {
  if(!images?.[0]?.isPrimary) {
    const primaryImage = images?.find((img) => img.isPrimary);

    const sortedImages = JSON.parse(JSON.stringify(images?.filter((img) => !img?.isPrimary)))?.sort((a, b) => {
      if(a.created_at === b.created_at) {
        return a?.imageOwnsId > b?.imageOwnsId ? 1 : a?.imageOwnsId < b?.imageOwnsId ? -1 : 0;
      }

      return a?.created_at > b?.created_at ? 1 : a?.created_at < b?.created_at ? -1 : 0;
    });

    if(primaryImage) {
      return [primaryImage, ...sortedImages];
    }

    if(sortedImages?.[0]) {
      Object.assign(sortedImages?.[0], {
        isPrimary: true,
      });
    }

    return sortedImages;
  }
  return images;
};

export const getBrowseItems = createSelector(
  getBrowseItemsInitial,
  getDeletedItemIds,
  (items, deletedIds) => items.map((item) => {
    return {
      ...item,
      isDeleted: deletedIds.includes(item.id),
      images: getImages(item?.images),
    };
  }),
);

export const getUpdatedBrowseItems = ({ id, data }) => createSelector(
  getBrowseItems,
  (items) => items.reduce((result, item) => {
    if(item.id !== id) {
      return [...result, {
        ...item,
        images: getImages(item?.images),
      }];
    }

    return [
      ...result,
      {
        ...data,
        item_name: data.name,
      },
    ];
  }, []),
);

export const getItemById = (id) => createSelector(
  getBrowseItems,
  (items) => items.find((item) => item.id === id),
);

export const getSelectedItemTypeIds = createSelector(
  getBrowseItems,
  getSelectedBrowseItems,
  (items, ids) => items
    .filter(({ id }) => ids.includes(id))
    .map(({ id }) => id),
);

export const getSelectedItemTypeNames = createSelector(
  getBrowseItems,
  getSelectedItemTypeIds,
  (items, ids) => items
    .filter(({ id }) => ids.includes(id))
    .map(({ title }) => title),
);

export const getSelectedSetTypeIds = createSelector(
  getBrowseItems,
  getSelectedBrowseItems,
  (items, ids) => items
    .filter(({ type, id }) => type === TYPE.SET && ids.includes(id))
    .map(({ id }) => id),
);

export const getSelectedSetTypeNames = createSelector(
  getBrowseItems,
  getSelectedSetTypeIds,
  (items, ids) => items
    .filter(({ id }) => ids.includes(id))
    .map(({ title }) => title),
);

export const getSelectedItems = createSelector(
  getBrowseItems,
  getSelectedBrowseItems,
  (items, ids) => items
    .filter(({ id }) => ids.includes(id)),
);

export const getQueryArray = createSelector(
  getBrowseItemsFilters,
  ({
    [FILTER_KEYS.PAGE]: page,
    [FILTER_KEYS.PER_PAGE]: perPage,
    [FILTER_KEYS.TYPE]: itemTtpe,
    [FILTER_KEYS.HAS_MORE]: hasMore,
    [FILTER_KEYS.SORT_DIRECTION]: sortDirection,
    ...queryParams
    // eslint-disable-next-line no-shadow,max-len
  }) => Object.keys(queryParams).reduce((result, type) => {
    switch (type) {
    case FILTER_KEYS.QUANTITY: {
      const isAnyQtyAllowed = queryParams[type] !== null;
      if (isAnyQtyAllowed) {
        return [
          ...result,
          {
            type,
            value: null,
            text: 'None',
          },
        ];
      }

      return result;
    }

    case FILTER_KEYS.QUERY: {
      const query = queryParams[type];

      if(query) {
        return [
          ...result,
          ...queryParams[type].split(',').map((entity) => ({ type, value: entity, text: entity })),
        ];
      }

      return result;
    }
    case FILTER_KEYS.TAGS:
    case FILTER_KEYS.ITEMS: {
      const isEmpty = queryParams[type].length === 0;
      if (!isEmpty) {
        return [
          ...result,
          ...queryParams[type].map((entity) => ({ type, value: entity.id, text: entity.name })),
        ];
      }

      return result;
    }
    case FILTER_KEYS.COLORS: {
      const isEmpty = queryParams[type].length === 0;
      if (!isEmpty) {
        return [
          ...result,
          ...queryParams[type].map((entity) => ({ type: 'color', value: entity.id })),
        ];
      }

      return result;
    }

    default: {
      return result;
    }
    }
  }, []),
);

export const getSuggestedBrowseItemsOptions = createSelector(
  getQueryArray,
  getSuggestedItemsOptions,
  (selectedQueryItems, suggestedQueryItems) => {
    const existingIds = selectedQueryItems.map(({ value }) => value);

    return suggestedQueryItems.filter((item) => !existingIds.includes(item.value));
  },
);

export const getBrowseItemsFormattedFilters = createSelector(
  getBrowseItemsFilters,
  (filters) => ({
    ...filters,
    [FILTER_KEYS.TAGS]: filters[FILTER_KEYS.TAGS].map(({ id }) => id),
    [FILTER_KEYS.ITEMS]: filters[FILTER_KEYS.ITEMS].map(({ id }) => id),
    [FILTER_KEYS.COLORS]: filters[FILTER_KEYS.COLORS].map(({ id }) => id),
  }),
);
